<div class="app-sidebar border-right-1 border-color-primary" [class.collapsed]="isCollapsed" *ngIf="currentUser$ | async as user">

    <!-- ═══════════════════════════════════════════════════════════ HEADER -->
    <div class="brand" *appDesktop>
        @if (!isCollapsed) {
            <!-- Expanded: full RISE SMS logo + collapse icon -->
            <a id="logo" class="app-logo" [routerLink]="['/']">
                <picture>
                    <source
                        srcset="assets/img/brand/rise-logo-long.svg"
                        media="(prefers-color-scheme: light), (prefers-color-scheme: no-preference)" />
                    <source
                        srcset="assets/img/brand/rise-dark-logo-long.svg"
                        media="(prefers-color-scheme: dark)" />
                    <img
                        class="menu-logo"
                        src="assets/img/brand/rise-logo-long.svg"
                        alt="App Logo" />
                </picture>
            </a>
            <button
                class="sidebar-toggle"
                (click)="sidebarStateService.toggle()"
                title="Collapse sidebar">
                <picture>
                    <source
                        srcset="assets/img/menu_collapse_dark.webp"
                        media="(prefers-color-scheme: dark)" />
                    <img src="assets/img/menu_collapse_light.webp" class="toggle-icon" alt="Collapse" />
                </picture>
            </button>
        } @else {
            <!-- Collapsed: R symbol only + expand icon below -->
            <a [routerLink]="['/']" class="app-logo-collapsed">
                <picture>
                    <source
                        srcset="assets/img/Tablet-Rise-Symbol-Dark.svg"
                        media="(prefers-color-scheme: dark)" />
                    <img
                        src="assets/img/Tablet-Rise-Symbol-Light.svg"
                        class="menu-logo-icon"
                        alt="RISE" />
                </picture>
            </a>
            <button
                class="sidebar-toggle"
                (click)="sidebarStateService.toggle()"
                title="Expand sidebar">
                <picture>
                    <source
                        srcset="assets/img/menu_expand_dark.webp"
                        media="(prefers-color-scheme: dark)" />
                    <img src="assets/img/menu_expand_light.webp" class="toggle-icon" alt="Expand" />
                </picture>
            </button>
        }
    </div>

    <!-- ═══════════════════════════════════════════════════════════ NAV -->
    <nav class="leftnav justify-content-between">
        <ul class="settings">

            <!-- Main menu items -->
            @for (item of (mainMenu$ | async)?.items; track item) {
                <li>
                    <ng-container *featureFlag="item?.feature">
                        <a
                            class="menu-item"
                            [class.disabled]="item.disabled"
                            [routerLink]="item.disabled ? null : item.routerLink"
                            [routerLinkActive]="item.disabled ? '' : 'active'"
                            [style.pointer-events]="item.disabled ? 'none' : 'auto'"
                            [style.opacity]="item.disabled ? '0.5' : '1'"
                            [title]="isCollapsed ? item.label : null"
                            appFeatureHighlight
                            [feature]="item.highlight">
                            <div>
                                @if (item.icon) {
                                    <img
                                        class="menu-icon"
                                        [src]="item.icon"
                                        alt="icon" />
                                }
                                @if (!isCollapsed) {
                                    <div class="menu-text">
                                        <div class="menu-label">{{ item.label }}</div>
                                        @if (item?.hasNotification$ | async) {
                                            <div class="notification"></div>
                                        }
                                        <app-feature-highlight-badge></app-feature-highlight-badge>
                                    </div>
                                }
                            </div>
                        </a>
                    </ng-container>
                </li>
            }

            <!-- Forms section -->
            @if (formMenu$ | async; as formMenu) {
                @if (formMenu.items.length > 0) {
                    <li>
                        <a
                            class="menu-item"
                            [class.section-label]="!isCollapsed"
                            [routerLink]="isCollapsed ? ['/assessments'] : null"
                            [routerLinkActive]="isCollapsed ? 'active' : ''"
                            [routerLinkActiveOptions]="{exact: true}"
                            [title]="isCollapsed ? formMenu.label : null">
                            <div>
                                <img
                                    class="menu-icon"
                                    [src]="formMenu.icon"
                                    alt="icon" />
                                @if (!isCollapsed) {
                                    <div class="menu-text">
                                        <div class="menu-label">{{ formMenu.label }}</div>
                                        <span
                                            class="menu-icon accordion"
                                            role="button"
                                            tabindex="0"
                                            [attr.aria-label]="showForms ? 'Collapse forms' : 'Expand forms'"
                                            (click)="toggleCollapse()"
                                            (keydown.enter)="toggleCollapse()"
                                            (keydown.space)="toggleCollapse()">
                                            <app-custom-icon
                                                [iconPath]="'assets/icons/general-icons.svg'"
                                                [imageName]="showForms ? 'chevron-up' : 'chevron-down'">
                                            </app-custom-icon>
                                        </span>
                                    </div>
                                }
                            </div>
                            @if (!isCollapsed && (isAtLeastAdmin$ | async) && !isAsap) {
                                <span
                                    class="menu-icon new-form"
                                    role="link"
                                    tabindex="0"
                                    aria-label="New form"
                                    [routerLink]="['/assessments', 'new']"
                                    [routerLinkActive]="'new-form-active'">
                                    <app-custom-icon
                                        [iconPath]="'assets/icons/general-icons.svg'"
                                        [imageName]="'plus-small'">
                                    </app-custom-icon>
                                </span>
                            }
                        </a>
                    </li>
                    @if (showForms && !isCollapsed) {
                        @for (item of formMenu.items; track item) {
                            <li>
                                <a
                                    class="menu-item"
                                    [routerLink]="item.routerLink"
                                    [routerLinkActive]="'active'"
                                    appFeatureHighlight
                                    [feature]="item.highlight"
                                    #link="routerLinkActive">
                                    <div>
                                        <app-list-icon [isActive]="link.isActive"></app-list-icon>
                                        <div class="menu-text">
                                            <div class="menu-label">{{ item.label }}</div>
                                            <app-feature-highlight-badge></app-feature-highlight-badge>
                                        </div>
                                    </div>
                                </a>
                            </li>
                        }
                    }
                }
            }

            <!-- Add/create button: collapsed nav, admin only -->
            @if (isCollapsed && (isAtLeastAdmin$ | async) && !isAsap) {
                <li>
                    <a
                        class="menu-item"
                        [routerLink]="['/assessments', 'new']"
                        [routerLinkActive]="'active'"
                        title="New Form">
                        <div>
                            <img
                                class="menu-icon"
                                src="assets/img/tablet_menu_form_templates.svg"
                                alt="New Form" />
                        </div>
                    </a>
                </li>
            }
        </ul>

        <!-- Bottom section: quick start + trial + promo -->
        <div class="sidebar-bottom">

            <!-- Expanded variants — [hidden] keeps setup-guide-button in DOM when collapsed so @ViewChild resolves -->
            <div [hidden]="isCollapsed">
                @if (hasNotDismissedBanner$ | async) {
                    <div class="px-3 mb-3">
                        <app-setup-guide-button
                            [hasNotDismissedBanner]="hasNotDismissedBanner$ | async"
                            [asapFormId$]="asapFormId$">
                        </app-setup-guide-button>
                    </div>
                }
                @if (isAsap && (isOwner$ | async)) {
                    <div class="my-3 px-3">
                        <app-promotional-banner
                            [show]="true"
                            [link]="['/profile/subscription']"
                            [canRedirect]="true"
                            title="Unlock the power of RISE"
                            subtitle="Compare available plans">
                        </app-promotional-banner>
                    </div>
                }
                @if (!isAsap) {
                    <app-trial-badge></app-trial-badge>
                }
            </div>

            <!-- Collapsed icon buttons -->
            @if (isCollapsed) {
                @if (hasNotDismissedBanner$ | async) {
                    <button
                        class="sidebar-icon-btn sidebar-quickstart-btn"
                        (click)="openSetupGuide($event)"
                        title="Quick Start Guide">
                        <img
                            src="assets/img/tablet-menu-quick-start.svg"
                            class="bottom-icon"
                            alt="Quick Start" />
                    </button>
                }
                @if (isTrialActive$ | async) {
                    <a
                        class="sidebar-icon-btn sidebar-trial-btn"
                        [routerLink]="['/profile/subscription/payment']"
                        title="Free Trial">
                        <img
                            src="assets/img/tablet-menu-trial-banner.svg"
                            class="bottom-icon"
                            alt="Free Trial" />
                    </a>
                }
            }
        </div>
    </nav>

    <!-- ═══════════════════════════════════════════════════════ USER CARD -->
    <div
        class="user-card flex align-items-center justify-content-between cursor-pointer border-top-1 absolute bottom-0 w-full"
        role="link"
        tabindex="0"
        aria-label="Go to profile"
        [routerLink]="['/profile']"
        [routerLinkActive]="'active'">
        @if (!isCollapsed) {
            <div class="flex flex-column max-w-12rem px-3 py-3">
                <h6
                    class="font-bold text-sm line-height-3 m-0 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                    >{{ user.firstName }} {{ user.lastName }}</h6
                >
                <p
                    class="text-xs line-height-3 m-0 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                    style="color: var(--text-read-only-color);"
                    >{{ user.email }}</p
                >
            </div>
            <i
                class="pi pi-chevron-right px-3"
                style="color: var(--text-read-only-color);"></i>
        } @else {
            <img
                src="assets/img/tablet-menu-account.svg"
                class="account-icon"
                alt="Account" />
        }
    </div>
</div>
